export interface PresenceScheduleInterval {
  start: string;
  end: string;
}

export interface PresenceSummaryZone {
  zone_name: string | null;
  absences: number;
  total_absence_sec: number;
  absent_now?: boolean;
  absent_for_sec?: number;
}

export const getPresenceTodayDate = (): string => new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

export const formatPresenceDuration = (seconds: number): string =>
  `${Math.floor(seconds / 3600)} ч ${Math.floor(seconds % 3600 / 60)} мин ${seconds % 60} с`;

export interface PresenceZone {
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Внутреннее представление (UI). */
export interface PresenceSettings {
  enabled: boolean;
  timeout: number;
  interval: number;
  schedule: PresenceScheduleInterval[];
  zones: PresenceZone[];
}

export const DEFAULT_PRESENCE_SETTINGS: PresenceSettings = {
  enabled: true,
  timeout: 60,
  interval: 300,
  schedule: [
    { start: '09:00', end: '12:00' },
    { start: '13:00', end: '18:00' },
  ],
  zones: [],
};

function parseZone(raw: unknown): PresenceZone | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const x = o.x;
  const y = o.y;
  const w = o.w;
  const h = o.h;
  if (
    typeof x === 'number' &&
    typeof y === 'number' &&
    typeof w === 'number' &&
    typeof h === 'number' &&
    w > 0 &&
    h > 0
  ) {
    return { name: typeof o.name === 'string' ? o.name : '', x, y, w, h };
  }
  return null;
}

function toTimeInputValue(value: string): string {
  const parts = value.trim().split(':');
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return value;
}

function parseSchedule(raw: unknown): PresenceScheduleInterval[] {
  if (!Array.isArray(raw)) return [];
  const out: PresenceScheduleInterval[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const start = typeof o.start === 'string' ? toTimeInputValue(o.start) : '';
    const end = typeof o.end === 'string' ? toTimeInputValue(o.end) : '';
    if (start && end) {
      out.push({ start, end });
    }
  }
  return out;
}

function readNonNegativeInt(...values: unknown[]): number | undefined {
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
      return Math.floor(v);
    }
  }
  return undefined;
}

/**
 * Тело PATCH /api/cameras/{camera_id}/presence (внешний API).
 * @see absence_timeout_sec, screenshot_interval_sec, schedule, zones
 */
export function toPresenceApiPayload(settings: {
  enabled?: boolean;
  timeout?: number;
  interval?: number;
  absenceTimeoutSec?: number;
  screenshotIntervalSec?: number;
  schedule?: PresenceScheduleInterval[];
  zones?: PresenceZone[];
  absence_timeout_sec?: number;
  screenshot_interval_sec?: number;
}): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if (settings.enabled !== undefined) {
    payload.enabled = settings.enabled;
  }

  const absenceTimeout =
    settings.absence_timeout_sec ?? settings.absenceTimeoutSec ?? settings.timeout;
  if (absenceTimeout !== undefined) {
    payload.absence_timeout_sec = absenceTimeout;
  }

  const screenshotInterval =
    settings.screenshot_interval_sec ?? settings.screenshotIntervalSec ?? settings.interval;
  if (screenshotInterval !== undefined) {
    payload.screenshot_interval_sec = screenshotInterval;
  }

  if (settings.schedule !== undefined) {
    payload.schedule = settings.schedule;
  }

  if (settings.zones !== undefined) {
    payload.zones = settings.zones;
  }

  return payload;
}

/** Нормализация ответа GET /presence. */
export function normalizePresenceSettings(data: unknown): PresenceSettings {
  const o = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const zones = Array.isArray(o.zones)
    ? o.zones.map(parseZone).filter((zone): zone is PresenceZone => zone !== null)
    : [];

  const timeout =
    readNonNegativeInt(o.absence_timeout_sec, o.timeout) ?? DEFAULT_PRESENCE_SETTINGS.timeout;
  const interval =
    readNonNegativeInt(o.screenshot_interval_sec, o.interval) ?? DEFAULT_PRESENCE_SETTINGS.interval;

  return {
    enabled: Boolean(o.enabled ?? o.active),
    timeout,
    interval,
    schedule: Array.isArray(o.schedule) ? parseSchedule(o.schedule) : [...DEFAULT_PRESENCE_SETTINGS.schedule],
    zones,
  };
}

/** Модели, для которых доступна проверка присутствия (тот же API /presence). */
export function isPresenceCheckModel(modelName: string): boolean {
  const key = (modelName || '').trim().toLowerCase();
  return key === 'people' || key === 'person' || key === 'utrau_staff_detector';
}

export function cameraHasPresenceCheckModel(models: string[] | undefined | null): boolean {
  return (models ?? []).some(isPresenceCheckModel);
}
